import { EmeraldError } from 'src/common/api/emerald-error';
import { AbortedError } from 'src/common/api/extended-emerald-error';
import { ItemAddType } from 'src/common/enumerations/item-add-type';
import { OneGroupItemNotSupported } from 'src/common/helpers/error-code';
import { ITareWeightPercentagesState } from 'src/common/interfaces/states/ITareWeightPercentageState';
import { TareWeightPercentagesActions } from 'src/common/redux/reducers/tare-weight-percentages-reducer';
import { LineItemAddDto } from 'src/items-list/business-logic/service/interfaces/line-item-add.dto';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { restorePage } from '../../common/components/emerald-home-page/emerald-home-page';
import { EntryMethod } from '../../common/enumerations/entry-method';
import { IItemLookup } from '../../common/interfaces/dtos/IItemLookup';
import { IManualTare } from '../../common/interfaces/dtos/IManualTare';
import { ITareWeightGroup } from '../../common/interfaces/states/ITareWeightGroupsState';
import { store } from '../../common/redux/configure-store';
import { lookupItemById } from '../../items-list/business-logic/item-lookup';
import { getLinkedItemsDetails } from '../../linked-item/handle-linked-item';
import { sellLinkedItem } from '../../linked-item/sell-linked-item';
import { getTareWeightGroup } from '../../linked-item/tare-weight-groups-operation';
import { popupMessage } from '../../messages/business-logic/popup-message';
import { ITareWeigthItemsProps, showTareWeightItemsScreen } from '../tare-weight-items';

interface HandleTareWeightItemsData {
  isTareModification: boolean;
  isManualTarePercentageEnabled: boolean;
  canManualTareWeight: boolean;
  grossWeight: number | undefined;
  tarePercentage: number[] | undefined;
  showPreDefinedOptionsForTareWeightItem: boolean;
  linkedGroupItems: IItemLookup[] | undefined;
}

export const handleTareWeightItems = async (
  lineItem: LineItemAddDto,
  itemLookup: IItemLookup,
  tareWeightItems?: IItemLookup[],
): Promise<void> => {
  const {
    isTareModification,
    isManualTarePercentageEnabled,
    canManualTareWeight,
    grossWeight,
    tarePercentage,
    showPreDefinedOptionsForTareWeightItem,
    linkedGroupItems,
  } = await getTareWeightItemsData(lineItem, itemLookup, tareWeightItems);

  return new Promise<void>((resolve, reject) => {
    const onClose = (): void => {
      reject(new AbortedError());
    };

    const onSubmit = async (value: number, isPercentage: boolean): Promise<void> => {
      const manualTare: IManualTare = { value, isPercentage };
      lineItem.itemAddType = ItemAddType.Tare;
      const tareLineItem = { ...lineItem, itemAddType: ItemAddType.Tare };
      return sellLinkedItem(tareLineItem, itemLookup, undefined, manualTare, isTareModification)
        .then(resolve)
        .catch(popupMessage);
    };

    const onSelect = async (tareItem: IItemLookup): Promise<void> => {
      const tareLineItem = { ...lineItem, itemAddType: ItemAddType.Tare };
      return sellLinkedItem(tareLineItem, itemLookup, tareItem, undefined, isTareModification)
        .then(resolve)
        .catch(popupMessage);
    };

    const onSelectPercentage = (tareValue: number): void => {
      onSubmit(tareValue, true);
    };

    const tareWeightItemsProps: ITareWeigthItemsProps = {
      linkedGroupItems,
      isTareModification: isTareModification,
      onClose,
      onSubmit,
      onSelect,
      onSelectPercentage,
      isManualTarePercentageEnabled,
      canManualTareWeight,
      grossWeight,
      tarePercentage: tarePercentage,
      showPreDefinedOptionsForTareWeightItem,
    };

    showTareWeightItemsScreen(tareWeightItemsProps);
  }).finally(() => restorePage());
};

export const getTareWeightItemsData = async (
  lineItem: LineItemAddDto,
  itemLookup: IItemLookup,
  tareWeightItems?: IItemLookup[],
): Promise<HandleTareWeightItemsData> => {
  let isTareModification = false;
  let isManualTarePercentageEnabled = true;
  let canManualTareWeight = true;
  const {
    manualTareAlwaysEnabled,
    showPreDefinedOptionsForTareWeightItem,
    allowModifyingTareForSingleGroupItem,
  } = getCurrentXLRConfig();
  if (!tareWeightItems) {
    isTareModification = true;
    if (lineItem.tareWeight) {
      const tareWeightGroup = await getLinkedTareWeightGroup(lineItem.item.id);
      if (tareWeightGroup) {
        if (!allowModifyingTareForSingleGroupItem) {
          validateLinkedItemsGroup(tareWeightGroup);
        }
        tareWeightItems = tareWeightGroup.linkedItems;
        if (!manualTareAlwaysEnabled) {
          isManualTarePercentageEnabled = !!tareWeightGroup.isManualTarePercentageEnabled;
          canManualTareWeight = !!store
            .getState()
            .commandMenu.rawCommands['ItemGroupActionCommands']?.commandsList?.find(
              c => c.name === 'AddManualTareCommand',
            );
        }
      }
    }
  } else if (!manualTareAlwaysEnabled) {
    isManualTarePercentageEnabled = !!itemLookup.isManualTarePercentageEnabled;
    canManualTareWeight = !!store
      .getState()
      .commandMenu.rawCommands['ItemGroupActionCommands']?.commandsList?.find(
        c => c.name === 'AddManualTareCommand',
      );
  }

  const grossWeight = lineItem.tareWeight?.grossWeight || lineItem.quantity.value;

  let tarePercentage: number[] | undefined = [];
  if (showPreDefinedOptionsForTareWeightItem) {
    tarePercentage = await getTarePercentages(itemLookup);
  }

  return {
    isTareModification,
    isManualTarePercentageEnabled,
    canManualTareWeight,
    grossWeight,
    tarePercentage,
    showPreDefinedOptionsForTareWeightItem,
    linkedGroupItems: tareWeightItems,
  };
};

const getLinkedTareWeightGroup = async (itemId: string): Promise<ITareWeightGroup | undefined> => {
  let tareWeightGroup = getTareWeightGroup(itemId);

  if (!tareWeightGroup) {
    const itemLookup = await lookupItemById({ data: itemId }, EntryMethod.Keyed, 'Sell');

    if (itemLookup.linkedItemsGroup?.linkedGroupCode) {
      tareWeightGroup = {
        linkedItems: (await getLinkedItemsDetails(itemLookup.linkedItemsGroup)).itemLookupData,
        isManualTarePercentageEnabled: itemLookup.isManualTarePercentageEnabled,
      };
    }
  }

  return tareWeightGroup;
};

const getTarePercentages = async (itemLookup: IItemLookup): Promise<number[] | undefined> => {
  const itemId = itemLookup.itemId ?? '';
  let tarePercentage: number[] | undefined;

  if (itemLookup.tarePercentage) {
    tarePercentage = getTarePercentageFromItemLookupObject(itemId, itemLookup, tarePercentage);
  } else {
    const tareWeightPercentages = store.getState().tareWeightPercentages;

    if (tareWeightPercentages[itemId]) {
      tarePercentage = getTarePercentageFromState(tarePercentage, tareWeightPercentages, itemId);
    } else {
      tarePercentage = await getTarePercentageFromLookupService(itemId, tarePercentage);
    }
  }

  return tarePercentage;
};

const addTareWeightPercentageToState = (itemId: string, itemLookup: IItemLookup): void => {
  if (itemLookup.tarePercentage && itemLookup.tarePercentage.length > 0) {
    store.dispatch(
      TareWeightPercentagesActions.addTareWeightPercentage(itemId, itemLookup.tarePercentage),
    );
  }
};

const getTarePercentageFromState = (
  tarePercentage: number[] | undefined,
  tareWeightPercentages: ITareWeightPercentagesState,
  itemId: string,
): number[] | undefined => {
  tarePercentage = tareWeightPercentages[itemId].tarePercentage;
  return tarePercentage;
};

const getTarePercentageFromLookupService = async (
  itemId: string,
  tarePercentage: number[] | undefined,
): Promise<number[] | undefined> => {
  const itemLookup = await lookupItemById({ data: itemId }, EntryMethod.Keyed, 'Sell');
  addTareWeightPercentageToState(itemId, itemLookup);

  tarePercentage = itemLookup.tarePercentage;
  return tarePercentage;
};

const getTarePercentageFromItemLookupObject = (
  itemId: string,
  itemLookup: IItemLookup,
  tarePercentage: number[] | undefined,
): number[] | undefined => {
  addTareWeightPercentageToState(itemId, itemLookup);

  tarePercentage = itemLookup.tarePercentage;
  return tarePercentage;
};
const validateLinkedItemsGroup = (tareWeightGroup: ITareWeightGroup): void => {
  /*
  Tare weight of an hard link item cannot be modified
  */
  if (tareWeightGroup.linkedItems.length === 1) {
    throw new EmeraldError(OneGroupItemNotSupported);
  }
};
